import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";

export interface CompanyConfig {
    companyName?: string;
    companyNameEn?: string;
    companyNameLegal?: string;
    address1?: string;
    address2?: string;
    taxCode?: string;
    email?: string;
    phone?: string;
    fax?: string;
    website?: string;
    bankAccount?: string;
    bankName?: string;
    bankBranch?: string;
    beneficiary?: string;
    logoUrl?: string;
    representative?: string;
    representativeTitle?: string;
}

function mapConfigToCompany(configData: Record<string, any> | null): CompanyConfig {
    if (!configData) return {};

    const get = (key: string): string | undefined => {
        const val = configData[key];
        if (val === undefined || val === null) return undefined;
        return typeof val === 'object' ? JSON.stringify(val) : String(val);
    };

    return {
        companyName: get('company_name'),
        companyNameEn: get('company_name_en'),
        companyNameLegal: get('company_name_legal'),
        address1: get('company_address_1'),
        address2: get('company_address_2'),
        taxCode: get('company_tax_code'),
        email: get('company_email'),
        phone: get('company_phone'),
        fax: get('company_fax'),
        website: get('company_website'),
        bankAccount: get('company_bank_account') || get('bank_account'),
        bankName: get('company_bank_name') || get('bank_name'),
        bankBranch: get('company_bank_branch') || get('bank_branch'),
        beneficiary: get('company_bank_beneficiary') || get('bank_beneficiary') || get('company_name'),
        logoUrl: get('company_logo'),
        representative: get('company_representative'),
        representativeTitle: get('company_representative_title'),
    };
}

export function useCompanyConfig(enabled = true) {
    const { data: configData } = useQuery({
        queryKey: ['config-all'],
        queryFn: async () => {
            try {
                return await trpcClient.config.getAll.query();
            } catch {
                return null;
            }
        },
        staleTime: 5 * 60 * 1000,
        enabled,
    });

    return mapConfigToCompany(configData as Record<string, any> | null);
}
